import React from 'react';
import { REMOTE_FALLBACK_COVER_URL } from '../constants/images';
import { resolveImageSrc, DEFAULT_AVATAR, avatarFallbackUrl } from '../utils/media';
import { DEFAULT_COVER } from '@/constants/media';
import { Phone, Mail, Globe, Share2, Radio, MapPin, Linkedin, Twitter, Instagram, Facebook, Music2, UserPlus } from 'lucide-react';
import { QrCode as QrCodeIcon } from 'lucide-react';
const QrCode = QrCodeIcon;
import UnifiedBrandIcon from './UnifiedBrandIcon';
import { User, AdConfig } from '../types';
import { QRCodeModal } from './QRCodeModal';
import { EnhancedEmailLeadForm } from './EnhancedEmailLeadForm';
import { BroadcastModal } from './BroadcastModal';
import BroadcastLoginModal from './BroadcastLoginModal';
import safeToast from '../utils/safeToast';
import { trackAdView, trackAdClick, adRedirectUrl } from '../api/analytics';
import { resolveUserColors } from '../utils/colors';
import { mockAds } from '../data/mockData';
import { listLeadsByOwner } from '../api/leads';
import { canonicalCardUrl } from '@/utils/url';
import { useShare } from '@/context/ShareProvider';
import { buildSharePayload } from '@/utils/share';

interface ECardTemplate1Props {
  user: User;
  ads: AdConfig[];
  onAnalyticsUpdate?: (userId: string, metric: string) => void;
  isOwnerView?: boolean;
  onLeadSubmit?: (leadData: any) => void;
  leads?: any[];
  onContactExchange?: () => void;
}

export const ECardTemplate1: React.FC<ECardTemplate1Props> = ({ user, ads, onAnalyticsUpdate, isOwnerView = false, onLeadSubmit, leads = [], onContactExchange }) => {
  const [showQRModal, setShowQRModal] = React.useState(false);
  const [showEmailForm, setShowEmailForm] = React.useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = React.useState(false);
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [currentAdIndex, setCurrentAdIndex] = React.useState(0);
  const [authedLeads, setAuthedLeads] = React.useState<any[] | null>(null);
  const { openShare } = useShare();

  // Ensure user has colors defined with defaults and valid hex
  const userColors = resolveUserColors(user.colors as any);

  // Use provided ads (topped up to min 3 with mocks), or fallback to mockAds when empty
  const sourceAds = React.useMemo(() => {
    const base = Array.isArray(ads) ? [...ads] : [];
    if (base.length === 0) return mockAds;
    if (base.length >= 3) return base;
    const out = [...base];
    try {
      const mocks = (mockAds as any[]).filter((m:any) => m && m.isActive !== false);
      for (const m of mocks) {
        if (out.length >= 3) break;
        const dupe = out.find((x:any) =>
          String(x?.title || '').toLowerCase() === String(m?.title || '').toLowerCase() ||
          String(x?.link || '') === String(m?.link || '')
        );
        if (!dupe) out.push(m as any);
      }
    } catch {}
    return out;
  }, [ads]);
  const activeAds = sourceAds.filter(ad => ad.isActive);
  const currentAd = activeAds.length > 0 ? activeAds[currentAdIndex] : null;
  const currentAdHref = React.useMemo(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      const href = (currentAd as any)?.link || (currentAd as any)?.url || (currentAd as any)?.href;
      if (id && href) return adRedirectUrl(String(id), String(href));
      return String(href || '#');
    } catch { return '#'; }
  }, [currentAd, currentAdIndex]);

  // Auto-scroll ads every 5 seconds
  React.useEffect(() => {
    if (activeAds.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prevIndex) => (prevIndex + 1) % activeAds.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeAds.length]);

  // Track ad view when the visible ad changes (ignore mocks without ids)
  React.useEffect(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      if (id) {
        try { trackAdView(String(id)).catch(() => {}); } catch {}
      }
    } catch {}
  }, [currentAdIndex]);

  const handleCall = () => {
    window.open(`tel:${user.phone}`);
    // Track as dedicated callClicks instead of lumping into contactClicks
    onAnalyticsUpdate?.(user.id, 'callClicks');
  };
  
  const handleEmail = () => {
    setShowEmailForm(true);
    onAnalyticsUpdate?.(user.id, 'messageClicks');
  };
  
  const handleWebsite = () => {
    window.open(user.website, '_blank');
    onAnalyticsUpdate?.(user.id, 'contactClicks');
  };
  
  const handleQR = () => {
    setShowQRModal(true);
    onAnalyticsUpdate?.(user.id, 'qrScans');
  };
  
  const handleShare = () => {
    const handle = (user as any)?.handle || (user as any)?.username || '';
    const username = (user as any)?.username || (user as any)?.id || '';
    const fallbackUrl = typeof window !== 'undefined' ? window.location.href : '';
    const cardUrl = (handle && username) ? canonicalCardUrl(handle, username) : (user.website || fallbackUrl);
    const payload = buildSharePayload({ name: user.name, url: cardUrl });
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent||'');
    if ((navigator as any)?.share && isMobile) {
      (navigator as any).share(payload).catch(() => openShare(cardUrl, payload.text));
    } else {
      openShare(cardUrl, payload.text);
    }
    onAnalyticsUpdate?.(user.id, 'shares');
  };
  
  // Broadcast click: deprecated path; keep for compatibility (unused)
  const handleBroadcast = () => setShowBroadcastModal(true);
  
  // Check if broadcast should be enabled (premium not expired)
  const _exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
  const _notExpired = !_exp || _exp > new Date();
  const _subActive = (user.subscription?.isActive ?? true) && _notExpired;
  const isBroadcastEnabled = isOwnerView && _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
  const isBroadcastOwnerEnabled = isOwnerView && _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
  const handleBroadcastClick = () => {
    // Non-eligible tiers: show soft info and do not open login
    const eligibleTier = _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
    if (!eligibleTier) { try { safeToast('info', 'This function only available for Gold or Platinum User, Subscribe Now !'); } catch {}; return; }
    if (isBroadcastOwnerEnabled) { setShowBroadcastModal(true); return; }
    setShowLoginModal(true);
  };
  
  const handleSaveCard = () => {
    // Create vCard data
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${user.name}
ORG:${user.company}
TITLE:${user.position}
TEL:${user.phone}
EMAIL:${user.email}
URL:${user.website}
NOTE:${user.bio}
END:VCARD`;

    // Create blob and download
    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
  link.download = `${(user.name || '').replace(/\s+/g, '-')}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
    
    onAnalyticsUpdate?.(user.id, 'saves');
  };
  
  const handleSocialClick = (platform: string) => {
    onAnalyticsUpdate?.(user.id, 'socialClicks');
  };

  return (
    <>
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
      {/* Section 1: Cover Image, Profile Pic, Name */}
      <div className="relative">
        <div 
          className="h-[200px] bg-gradient-to-r from-blue-500 to-purple-600 overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${userColors.primary}, ${userColors.secondary})` 
          }}
        >
          {(() => {
            const customCover = Boolean((user as any).coverImage || (user as any).coverUrl);
            const coverSrc = (user as any).coverImage || (user as any).coverUrl || DEFAULT_COVER;
            // When falling back to DEFAULT_COVER, render it at full strength (no blend/opacity)
            const imgClass = `w-full h-full object-cover ${customCover ? 'mix-blend-overlay opacity-50' : ''}`.trim();
            return (
              <img 
                src={coverSrc}
                alt="Cover" 
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  const def = String(DEFAULT_COVER);
                  const remote = String(REMOTE_FALLBACK_COVER_URL);
                  if (img.src !== def) {
                    img.src = def;
                    // Remove blend/opacity if we fell back to DEFAULT_COVER
                    e.currentTarget.className = `w-full h-full object-cover`;
                  } else if (img.src !== remote) {
                    img.src = remote;
                    e.currentTarget.className = `w-full h-full object-cover`;
                  }
                }}
                className={imgClass}
                style={{ objectPosition: 'center 15%' }}
              />
            );
          })()}
        </div>
        
  {/* Strip Line removed to avoid visible seam in tile previews */}
        
  <div className="absolute -bottom-18 left-1/2 transform -translate-x-1/2">
          <div className="relative">
            <img 
              src={user?.avatar ? resolveImageSrc(user.avatar) : avatarFallbackUrl(user?.name, (user as any)?.username)} 
              alt={user.name}
              className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
              onError={(e) => { const img = e.currentTarget as HTMLImageElement; const fb = avatarFallbackUrl(user?.name, (user as any)?.username); if (img.src !== fb) img.src = fb; }}
            />
            <div 
              className="absolute -bottom-2 -right-2 px-2 py-1 rounded-full text-xs font-semibold text-white shadow-lg"
              style={{ backgroundColor: userColors.companyBadge }}
            >
              {(user.company || '').split(' ')[0] || 'Company'}
            </div>
          </div>
        </div>
      </div>

      {/* Strip Line: visible below cover */}
      <div 
        className="h-1 w-full"
        style={{ backgroundColor: userColors.stripLine }}
      ></div>

  <div className="pt-24 px-6">
        <div className="text-center mb-6">
          <h1 
            className="text-2xl font-bold mb-1"
          >
            {user.name}
          </h1>
          <p 
            className="mb-1"
            style={{ color: userColors.primary }}
          >
            {user.position}
          </p>
          <p 
            className="text-sm"
            style={{ color: userColors.secondary }}
          >
            {user.company}
          </p>
        </div>

        {/* Section 2: Action Buttons */}
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <button 
              onClick={handleCall}
              className="flex flex-col items-center p-3 rounded-lg border-2 hover:shadow-md transition-all"
              style={{ 
                borderColor: userColors.primary,
                color: userColors.primary 
              }}
            >
              <Phone className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Call</span>
            </button>
            <button 
              onClick={handleEmail}
              className="flex flex-col items-center p-3 rounded-lg border-2 hover:shadow-md transition-all"
              style={{ 
                borderColor: userColors.primary,
                color: userColors.primary 
              }}
            >
              <Mail className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Email</span>
            </button>
            <button 
              onClick={handleWebsite}
              className="flex flex-col items-center p-3 rounded-lg border-2 hover:shadow-md transition-all"
              style={{ 
                borderColor: userColors.primary,
                color: userColors.primary 
              }}
            >
              <Globe className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Website</span>
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <button 
              onClick={handleQR}
              className="flex flex-col items-center p-3 rounded-lg border-2 hover:shadow-md transition-all cursor-pointer"
              style={{ 
                borderColor: userColors.secondary,
                color: userColors.secondary 
              }}
            >
              <QrCode className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">QR Code</span>
            </button>
            <button 
              onClick={handleShare}
              className="flex flex-col items-center p-3 rounded-lg border-2 hover:shadow-md transition-all cursor-pointer"
              style={{ 
                borderColor: userColors.secondary,
                color: userColors.secondary 
              }}
            >
              <Share2 className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Share</span>
            </button>
            <button 
              onClick={handleBroadcastClick}
              className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:shadow-md`}
              style={{ 
                borderColor: userColors.secondary,
                color: userColors.secondary
              }}
            >
              <Radio className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Broadcast</span>
            </button>
          </div>
        </div>

        {/* Section 3: Bio Info */}
        <div className="mb-6">
          <h3 
            className="text-lg font-semibold mb-2"
            style={{ color: userColors.text }}
          >
            About
          </h3>
          <div 
            className="p-3 rounded-lg text-sm leading-relaxed"
            style={{ 
              backgroundColor: userColors.background,
              color: userColors.text 
            }}
          >
            {user.bio}
          </div>
        </div>

        {/* Separator */}
        <div className="mb-6">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        </div>

        {/* Section 4: Contact & Social */}
        <div className="mb-6">
          <h3 
            className="text-lg font-semibold mb-3"
            style={{ color: userColors.text }}
          >
            Contact
          </h3>
          <div className="space-y-2 mb-4">
            <div 
              className="flex items-center text-sm"
              style={{ color: userColors.text }}
            >
              <Phone className="w-4 h-4 mr-2" style={{ color: userColors.primary }} />
              {user.phone}
            </div>
            <div 
              className="flex items-center text-sm"
              style={{ color: userColors.text }}
            >
              <Mail className="w-4 h-4 mr-2" style={{ color: userColors.primary }} />
              {user.email}
            </div>
            <div 
              className="flex items-center text-sm"
              style={{ color: userColors.text }}
            >
              <Globe className="w-4 h-4 mr-2" style={{ color: userColors.primary }} />
              {user.website}
            </div>
          </div>

          <h3 
            className="text-lg font-semibold mb-3"
            style={{ color: userColors.text }}
          >
            Social
          </h3>
          
          {/* Mini separator between Contact and Social */}
          <div className="mb-4">
            <div className="w-16 h-px bg-gray-200 mx-0"></div>
          </div>
          
          <div className="flex space-x-3">
            {(() => {
              const PRIORITY = ['facebook','instagram','tiktok','twitter','linkedin'];
              const normalize = (s: string) => String(s || '').trim().toLowerCase();
              const alias = (p: string, url?: string) => {
                const k = normalize(p);
                const u = normalize(url || '');
                if (k === 'x' || k.includes('x.com') || u.includes('x.com')) return 'twitter';
                if (k.includes('twitter') || u.includes('twitter.com')) return 'twitter';
                return k;
              };
              const ALLOWED = new Set(['facebook','instagram','tiktok','twitter','linkedin']);
              const entriesRaw = Object.entries((user as any).socialLinks || (user as any).social || {})
                .filter(([_, url]) => url && String(url).trim() !== '');
              const entries = entriesRaw
                .map(([p, u]) => [alias(String(p), String(u)), u] as [string, any])
                .filter(([p]) => ALLOWED.has(String(p)));
              entries.sort((a, b) => PRIORITY.indexOf(String(a[0])) - PRIORITY.indexOf(String(b[0])));
              return entries.slice(0, 3).map(([platform, url]) => {
                return (
                  <a 
                    key={String(platform)}
                    href={String(url)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => handleSocialClick(String(platform))}
                  >
                    <UnifiedBrandIcon platform={String(platform)} kind="social" size={24} title={String(platform)} className="" color={userColors.accent} />
                  </a>
                );
              });
            })()}
          </div>

          {/* Streaming Links (if any) */}
          {Object.keys((user as any).streamingLinks || {}).length > 0 && (
            <div className="mt-3 flex space-x-3">
              {Object.entries((user as any).streamingLinks || {})
                .filter(([platform, url]) => {
                  const p = String(platform).toLowerCase();
                  const allowed = new Set(['spotify','youtube','soundcloud','boomplay','deezer','tidal','audiomack']);
                  return url && String(url).trim() !== '' && allowed.has(p);
                })
                .slice(0, 4)
                .map(([platform, url]) => (
                  <a
                    key={platform}
                    href={String(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 text-gray-700"
                    onClick={() => onAnalyticsUpdate?.(user.id, 'socialClicks')}
                  >
                    <UnifiedBrandIcon platform={String(platform)} kind="streaming" size={14} title={String(platform)} className="" color={userColors.accent} />
                    <span>{String(platform)}</span>
                  </a>
                ))}
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="mb-6">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        </div>

        {/* Contact Exchange Button */}
        {onContactExchange && !isOwnerView && (
          <button
            onClick={onContactExchange}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-white mb-3 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: userColors.primary }}
          >
            <UserPlus className="w-4 h-4" />
            Send {user.name?.split(' ')[0] || 'them'} your contact
          </button>
        )}

        {/* Section 5: Save Card Button */}
        <button 
          onClick={handleSaveCard}
          className="w-full flex items-center justify-center py-2 rounded-lg font-medium text-white mb-6 hover:opacity-90 transition-opacity"
          style={{ backgroundColor: userColors.saveButton }}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M5 20h14" />
          </svg>
          Save Card
        </button>

        {/* Section 6: Ads Space */}
        {currentAd && (
          <div className="pt-4 mb-4">
            {/* Separator */}
            <div className="mb-4">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
            </div>
            <div className="text-center relative overflow-hidden">
              <p className="text-xs text-gray-500 mb-2">Sponsored</p>
              <div className="relative">
                <a 
                  href={currentAdHref}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block transition-all duration-500 ease-in-out"
                  key={currentAdIndex}
                  onClick={() => { try { const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId; if (id) { try { trackAdClick(String(id)).catch(() => {}); } catch {} } } catch {} }}
                >
                  <div className="w-full" style={{ height: 140 }}>
                    <img 
                      src={currentAd.image} 
                      alt={currentAd.title}
                      className="w-full h-20 object-cover rounded-lg mb-2 transition-opacity duration-500"
                    />
                    <h4 className="text-sm font-semibold text-gray-900 transition-opacity duration-500 truncate" title={currentAd.title}>{currentAd.title}</h4>
                    <p className="text-xs text-gray-600 transition-opacity duration-500 line-clamp-2" title={currentAd.description}>{currentAd.description}</p>
                  </div>
                </a>
                
                {/* Ad indicators */}
                {activeAds.length > 1 && (
                  <div className="flex justify-center mt-2 space-x-1">
                    {activeAds.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          index === currentAdIndex ? 'bg-gray-600' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        user={user}
      />

      <EnhancedEmailLeadForm
        isOpen={showEmailForm}
        onClose={() => setShowEmailForm(false)}
        recipientName={user.name}
        recipientEmail={user.email}
        cardOwnerId={user.id}
        onLeadSubmit={onLeadSubmit}
        onAnalyticsUpdate={() => onAnalyticsUpdate?.(user.id, 'formSubmissions')}
      />

    <BroadcastLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={async ({ id }) => {
          const uid = String(user.id);
          if (String(id) !== uid) { safeToast('error', 'This account does not own this card.'); return; }
          const exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
          const active = (user.subscription?.isActive ?? true) && (!exp || exp > new Date());
      if (!active || !(['gold','platinum'].includes(String(user.subscription?.type)))) { safeToast('warning', 'Broadcast requires an active Gold or Platinum subscription.'); return; }
          try { const fresh = await listLeadsByOwner(String(uid)).catch(() => null); if (fresh && Array.isArray(fresh)) setAuthedLeads(fresh as any); } catch {}
          setShowBroadcastModal(true);
          try { onAnalyticsUpdate?.(user.id, 'messageClicks'); } catch {}
        }}
      />

      <BroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
        userName={user.name}
        cardOwnerId={user.id}
  leads={(authedLeads ?? leads) as any}
      />
  {/* share popup handled by global ShareProvider */}
    </>
  );
};